"use client";

import { useMemo, useRef, useState } from "react";
import Link from "@/components/ui/intent-link";

import { useFaqDrawer } from "@/lib/faq-context";
import { useRovingFocus } from "@/lib/use-roving-focus";
import { resolveTopicName, type FaqTopic } from "@/lib/faq-topics";
import { faqInRegion, faqUrl, topicSlug, topicUrl, type FaqStory } from "@/lib/faqs";
import type { Region } from "@/lib/region";
import { normalizeQuery, tokenizeQuery } from "@/lib/search";

const RESULTS_PER_PAGE = 12;

function SearchIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="9" cy="9" r="6" />
      <path d="m17 17-3.5-3.5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function ChevronToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform duration-200 ease-out ${open ? "rotate-90" : ""}`}
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export default function FaqIndexPage({
  faqs: initialFaqs,
  topics,
  region,
}: {
  faqs: FaqStory[];
  topics: FaqTopic[];
  region: Region;
}) {
  const { faqs: contextFaqs } = useFaqDrawer();
  const faqs = useMemo(() => {
    const byUuid = new Map(initialFaqs.map((f) => [f.uuid, f]));
    for (const f of contextFaqs) byUuid.set(f.uuid, f);
    return Array.from(byUuid.values()).filter((f) => faqInRegion(f, region));
  }, [contextFaqs, initialFaqs, region]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const faqsByTopic = useMemo(() => {
    const m = new Map<string, FaqStory[]>();
    for (const faq of faqs) {
      const slug = topicSlug(faq.content?.topic);
      if (!slug) continue;
      const list = m.get(slug);
      if (list) list.push(faq);
      else m.set(slug, [faq]);
    }
    return m;
  }, [faqs]);

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const [slug, list] of faqsByTopic) m.set(slug, list.length);
    return m;
  }, [faqsByTopic]);

  const displayTopics = useMemo<FaqTopic[]>(
    () =>
      Array.from(faqsByTopic.keys())
        .map((value) => ({ value, name: resolveTopicName(value, topics) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [faqsByTopic, topics],
  );

  const normalised = normalizeQuery(query);
  const tokens = useMemo(() => tokenizeQuery(normalised), [normalised]);
  const matches = useMemo(() => {
    if (!normalised || tokens.length === 0) return [];
    return faqs.filter((faq) => {
      const name = faq.name.toLowerCase();
      return tokens.every((t) => name.includes(t));
    });
  }, [faqs, normalised, tokens]);

  const totalPages = Math.max(1, Math.ceil(matches.length / RESULTS_PER_PAGE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pagedMatches = matches.slice(
    (currentPage - 1) * RESULTS_PER_PAGE,
    currentPage * RESULTS_PER_PAGE,
  );

  const setQueryAndReset = (next: string) => {
    setQuery(next);
    setPage(1);
  };

  return (
    <article>
      <section className="bg-teal-02 px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20">
        <div className="mx-auto w-full max-w-160">
          <div className="text-center">
            <h1 className="text-page-title text-teal-08">Frequently asked questions</h1>
            <p className="text-grey-07 mt-4 text-base leading-relaxed tracking-[-0.01em] lg:text-xl">
              Quick answers to the most common questions about eye surgery at Optilase, from
              suitability and the procedures themselves to recovery, pricing and what to expect on
              the day.
            </p>
          </div>

          <div className="mt-8 lg:mt-10">
            <div className="relative">
              <span className="text-teal-07 pointer-events-none absolute top-1/2 left-5 -translate-y-1/2">
                <SearchIcon />
              </span>
              <input
                type="search"
                name="faq-search"
                value={query}
                onChange={(e) => setQueryAndReset(e.target.value)}
                placeholder="Search for a question…"
                aria-label="Search FAQs"
                autoComplete="off"
                className="text-grey-06 placeholder:text-grey-06 focus:border-teal-07 h-14 w-full rounded-full border border-transparent bg-white pr-14 pl-12 text-base transition-shadow outline-none focus:shadow-[0_4px_20px_rgba(0,133,124,0.18)] [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQueryAndReset("")}
                  aria-label="Clear search"
                  className="absolute top-1/2 right-3 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/5 text-black/60 transition-colors hover:bg-black/10 hover:text-black"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden
                  >
                    <path d="m3 3 8 8M11 3l-8 8" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-360 px-5 py-10 md:px-10 lg:px-20 lg:py-20">
        {normalised ? (
          <SearchResults
            matches={matches}
            pagedMatches={pagedMatches}
            query={normalised}
            region={region}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : (
          displayTopics.length > 0 && (
            <TopicsGrid
              topics={displayTopics}
              counts={counts}
              faqsByTopic={faqsByTopic}
              region={region}
            />
          )
        )}
      </div>
    </article>
  );
}

function SearchResults({
  matches,
  pagedMatches,
  query,
  region,
  currentPage,
  totalPages,
  onPageChange,
}: {
  matches: FaqStory[];
  pagedMatches: FaqStory[];
  query: string;
  region: Region;
  currentPage: number;
  totalPages: number;
  onPageChange: (n: number) => void;
}) {
  const { ref: gridRef, rovingProps } = useRovingFocus<HTMLUListElement>();
  const resultsTopRef = useRef<HTMLSpanElement>(null);

  const goToPage = (n: number) => {
    onPageChange(n);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      resultsTopRef.current?.focus({ preventScroll: true });
    });
  };

  if (matches.length === 0) {
    return (
      <div className="rounded-2xl border border-black/10 bg-white p-8 text-center">
        <p className="text-lg font-bold text-black">No questions match &ldquo;{query}&rdquo;</p>
        <p className="mt-2 text-base text-black/60">Try a different word or browse topics below.</p>
      </div>
    );
  }
  return (
    <section>
      <span ref={resultsTopRef} tabIndex={-1} className="sr-only" />
      <h2 className="text-h4 text-teal-08 lg:text-h2 leading-[1.12] font-bold lg:leading-[1.054]">
        {matches.length} {matches.length === 1 ? "result" : "results"} for &ldquo;{query}&rdquo;
      </h2>
      <ul ref={gridRef} className="mt-6 grid gap-3 md:grid-cols-2" {...rovingProps}>
        {pagedMatches.map((faq) => (
          <FaqRow key={faq.uuid} faq={faq} region={region} />
        ))}
      </ul>
      {totalPages > 1 && (
        <Pagination current={currentPage} total={totalPages} onChange={goToPage} />
      )}
    </section>
  );
}

function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const set = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...set].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

function Pagination({
  current,
  total,
  onChange,
}: {
  current: number;
  total: number;
  onChange: (n: number) => void;
}) {
  const window = pageWindow(current, total);
  const linkClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 cursor-pointer items-center justify-center rounded-full border border-black/10 px-3 text-sm font-semibold text-teal-08 transition-colors hover:border-teal-06 hover:text-teal-07";
  const activeClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full bg-teal-07 px-3 text-sm font-semibold text-white";
  const disabledClass =
    "inline-flex h-9 min-w-9 sm:h-10 sm:min-w-10 items-center justify-center rounded-full border border-black/10 px-3 text-sm font-semibold text-black/30";

  const homeIndex =
    (current > 1 ? 1 : 0) + window.filter((w) => typeof w === "number" && w < current + 1).length;
  const { ref: navRef, rovingProps } = useRovingFocus<HTMLElement>({
    homeIndex,
  });

  return (
    <nav
      ref={navRef}
      aria-label="Search results pagination"
      className="mt-10 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
      {...rovingProps}
    >
      {current > 1 ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current - 1)}
          aria-label="Previous page"
          className={linkClass}
        >
          ←
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          ←
        </span>
      )}
      {window.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} aria-hidden className="px-1 text-black/55">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            data-roving
            onClick={() => onChange(p)}
            aria-current={p === current ? "page" : undefined}
            className={p === current ? activeClass : linkClass}
          >
            {p}
          </button>
        ),
      )}
      {current < total ? (
        <button
          type="button"
          data-roving
          onClick={() => onChange(current + 1)}
          aria-label="Next page"
          className={linkClass}
        >
          →
        </button>
      ) : (
        <span aria-hidden className={disabledClass}>
          →
        </span>
      )}
    </nav>
  );
}

function FaqRow({ faq, region }: { faq: FaqStory; region: Region }) {
  return (
    <li>
      <Link
        href={faqUrl(faq, region)}
        data-roving
        className="group hover:border-teal-06 hover:bg-teal-01 flex h-full items-center justify-between gap-4 rounded-[12px] border border-black/10 bg-white px-5 py-4 transition-colors lg:px-5 lg:py-5"
      >
        <span className="text-base leading-tight font-semibold text-black lg:text-[17px]">
          {faq.name}
        </span>
        <span className="text-teal-07 shrink-0">
          <ArrowIcon />
        </span>
      </Link>
    </li>
  );
}

function TopicsGrid({
  topics,
  counts,
  faqsByTopic,
  region,
}: {
  topics: FaqTopic[];
  counts: Map<string, number>;
  faqsByTopic: Map<string, FaqStory[]>;
  region: Region;
}) {
  const [openTopic, setOpenTopic] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState(0);
  const [activeQuestion, setActiveQuestion] = useState(0);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const questionRefs = useRef<(HTMLAnchorElement | null)[][]>([]);

  const toggleCard = (value: string) => {
    setOpenTopic((o) => (o === value ? null : value));
    setActiveQuestion(0);
  };

  const goToCard = (next: number) => {
    setActiveCard(next);
    setOpenTopic(null);
    setActiveQuestion(0);
    headerRefs.current[next]?.focus();
  };

  const totalCount = useMemo(() => {
    let total = 0;
    for (const list of faqsByTopic.values()) total += list.length;
    return total;
  }, [faqsByTopic]);

  return (
    <section>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-h4 text-grey-08">Browse by topic</h2>
        <span className="text-sm text-black/55">
          {totalCount} {totalCount === 1 ? "question" : "questions"}
        </span>
      </div>
      <ul className="mt-6 lg:mt-8 [@media(min-width:680px)]:columns-2 [@media(min-width:680px)]:gap-6">
        {topics.map((topic, index) => {
          const count = counts.get(topic.value) ?? 0;
          const isOpen = openTopic === topic.value;
          const topicFaqs = faqsByTopic.get(topic.value) ?? [];
          return (
            <li key={topic.value} className="mb-4 break-inside-avoid">
              <div
                className={`has-focus-visible:outline-teal-07 overflow-hidden rounded-2xl border transition-colors has-focus-visible:outline-2 has-focus-visible:outline-offset-2 ${
                  isOpen
                    ? "bg-teal-01 border-transparent"
                    : "border-grey-01 hover:border-teal-06 bg-white"
                }`}
              >
                <div
                  ref={(el) => {
                    headerRefs.current[index] = el;
                  }}
                  role="button"
                  tabIndex={index === activeCard ? 0 : -1}
                  onFocus={() => setActiveCard(index)}
                  onClick={() => toggleCard(topic.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleCard(topic.value);
                    } else if (e.key === "Escape" && isOpen) {
                      e.preventDefault();
                      setOpenTopic(null);
                    } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                      e.preventDefault();
                      goToCard(Math.min(index + 1, topics.length - 1));
                    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                      e.preventDefault();
                      goToCard(Math.max(index - 1, 0));
                    }
                  }}
                  aria-expanded={isOpen}
                  className={`flex w-full cursor-pointer justify-between gap-4 px-5 pt-5 text-left focus-visible:outline-none lg:px-6 lg:pt-6 ${
                    isOpen ? "items-start pb-3" : "items-center pb-5 lg:pb-6"
                  }`}
                >
                  <span>
                    <span className="text-grey-08 text-lg leading-normal font-bold tracking-[-0.01em] lg:text-xl">
                      {topic.name}
                    </span>
                    <span className="text-grey-06 mt-2 block text-base leading-normal lg:text-lg">
                      {count} {count === 1 ? "question" : "questions"}
                    </span>
                  </span>
                  <span className="text-teal-07 shrink-0">
                    <ChevronToggleIcon open={isOpen} />
                  </span>
                </div>
                <div
                  inert={!isOpen}
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 pt-3 pb-5 lg:px-6 lg:pb-6">
                      <ul className="marker:text-grey-08 list-disc ps-6">
                        {topicFaqs.map((faq, qIndex) => (
                          <li key={faq.uuid} className="mb-3 last:mb-0">
                            <Link
                              href={faqUrl(faq, region)}
                              ref={(el) => {
                                (questionRefs.current[index] ??= [])[qIndex] = el;
                              }}
                              tabIndex={qIndex === activeQuestion ? 0 : -1}
                              onKeyDown={(e) => {
                                const list = questionRefs.current[index] ?? [];
                                if (e.key === "ArrowDown") {
                                  e.preventDefault();
                                  const next = Math.min(qIndex + 1, list.length - 1);
                                  setActiveQuestion(next);
                                  list[next]?.focus();
                                } else if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  const prev = Math.max(qIndex - 1, 0);
                                  setActiveQuestion(prev);
                                  list[prev]?.focus();
                                } else if (e.key === "Escape") {
                                  e.preventDefault();
                                  setOpenTopic(null);
                                  headerRefs.current[index]?.focus();
                                }
                              }}
                              className="text-grey-08 hover:text-teal-08 block text-base leading-relaxed tracking-[-0.01em] transition-colors hover:font-semibold"
                            >
                              {faq.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link
                        href={topicUrl(topic.value, region)}
                        tabIndex={isOpen ? 0 : -1}
                        className="text-teal-07 hover:text-teal-08 mt-4 inline-flex items-center gap-2 text-base font-bold transition-colors"
                      >
                        All {topic.name} questions
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
